/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, AutoComplete, Switch } from 'antd'
import {
  CloseOutlined,
  GlobalOutlined,
  SearchOutlined,
  UserAddOutlined,
} from '@ant-design/icons'
import debounce from 'lodash/debounce'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'
import type { AdminPlayerSearchResult } from '@sportninja/common/services/player/searchPlayers'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import PersonSearchIcon from 'src/components/ts-components/icons/PersonSearchIcon'
import useWindowSize from 'src/hooks/useWindowSize'
import {
  usePlayerSearch,
  canShowGlobalSearchToggle,
  globalSearchToggleLabel,
} from '../hooks/usePlayerSearch'
import { getPlayerSearchCardState } from '../utils/playerSearchCardState'

// Search field border — Figma secondary-50 (#48556a); not in appColors.
const SEARCH_FIELD_BORDER = '#48556a'

// SECONDARY_150 / SECONDARY_350 are absent from the typed appColors inference
// (the JS module drops them); read them via a cast, the established pattern.
const palette = colors as Record<string, string>

const PLACEHOLDER = '/images/generic_placeholders/placeholder_player.png'

interface Props {
  isOpen: boolean
  onClose: () => void
  organizationId?: string
  /** Account id, for SN-7578 account-scoped "global" search on non-Canlan. */
  accountId?: string
  /** Team name shown in the title: "Add Player - {teamName}". */
  teamName?: string
  /**
   * Player ids already on the roster — filtered out of the search results so a
   * player can't be added twice. Compared against each result's player `id`.
   */
  excludePlayerIds?: string[]
  /** A search result was picked → add that existing player to the roster. */
  /** A search result was picked — pass the full row (id, name, email, image). */
  onSelectExisting: (player: AdminPlayerSearchResult) => void
  /** "New Player" pressed → open the blank create form. */
  onCreateNew: () => void
  /**
   * The z-index of the overlay this modal is opened FROM (the Operations tab's
   * full-screen shell). The modal then stacks one layer above it. Absent on a
   * page, where antd's default layer applies.
   */
  hostZIndex?: number
}

/**
 * Roster "Add Player" entry point. Search existing players (org-scoped, or
 * Global Search across orgs) and pick one — OR start a brand-new player. Both
 * paths hand off to the full-screen RosterPlayerModal (the parent wires the
 * handlers). Styled to match the AdminPlayerRegisterModal player section.
 */
const PlayerSearchModal: React.FC<Props> = ({
  isOpen,
  onClose,
  organizationId,
  accountId,
  teamName,
  excludePlayerIds,
  onSelectExisting,
  onCreateNew,
  hostZIndex,
}) => {
  const { width } = useWindowSize()
  const isMobile = width <= 768

  // SN-7578: only show the toggle when a broaden path exists (true-global tenant
  // or a resolvable account); its label/scope differ per tenant.
  const showGlobalToggle = canShowGlobalSearchToggle(accountId)
  const [isGlobalSearch, setIsGlobalSearch] = useState(false)
  const [query, setQuery] = useState('')
  // The text shown in the AutoComplete input (controlled so it never retains a
  // previously selected player id between opens).
  const [inputValue, setInputValue] = useState('')
  const selectRef = useRef<{ blur?: () => void } | null>(null)

  // Reset all search state whenever the modal opens, so a prior selection (id)
  // never lingers in the field.
  useEffect(() => {
    if (isOpen) {
      setIsGlobalSearch(false)
      setQuery('')
      setInputValue('')
    }
  }, [isOpen])

  const { results, isSearching } = usePlayerSearch({
    query,
    organizationId,
    accountId,
    isGlobal: isGlobalSearch,
  })

  // Debounce the query that feeds the (React Query) hook; the hook keys on the
  // settled value so each search caches independently.
  const setQueryDebounced = useMemo(
    () => debounce((value: string) => setQuery(value), 400),
    []
  )
  const handleSearch = useCallback(
    (value: string) => {
      setInputValue(value)
      setQueryDebounced(value)
    },
    [setQueryDebounced]
  )

  // Players already on the roster are dropped from the results so the same
  // player can't be added twice.
  const excludedIds = useMemo(
    () => new Set(excludePlayerIds ?? []),
    [excludePlayerIds]
  )

  const options = useMemo(
    () =>
      results
        .filter((p) => !excludedIds.has(p.id))
        .map((player) => ({
          value: player.id,
          label: <PlayerOption player={player} />,
        })),
    [results, excludedIds]
  )

  // Drives the empty/no-results card below the field. Results themselves stay
  // in the antd dropdown popup; the card hides once there are results.
  const cardState = getPlayerSearchCardState({
    query,
    isSearching,
    resultCount: options.length,
  })

  const handleSelect = useCallback(
    (playerId: string) => {
      selectRef.current?.blur?.()
      const picked = results.find((p) => p.id === playerId)
      if (picked) {
        onSelectExisting(picked)
      }
    },
    [onSelectExisting, results]
  )

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      centered={!isMobile}
      maskClosable={false}
      zIndex={hostZIndex === undefined ? undefined : hostZIndex + 1}
      width={isMobile ? '100%' : 560}
      css={modalCss}
      closeIcon={<CloseOutlined css={closeIconStyle} />}
      title={
        <Text
          variant='heading3'
          weight='semiBold'
          color={colors.WHITE}
          style='margin: 0;'
        >
          {capitalizeWords('Add Player')}
          {teamName ? ` - ${teamName}` : ''}
        </Text>
      }
      footer={
        <div css={footerStyle}>
          <div css={footerButtonStyle}>
            <Button
              text='New Player'
              onClick={onCreateNew}
              variant='secondary'
              style={newPlayerButtonStyle}
              icon={<UserAddOutlined css={newPlayerIconStyle} />}
            />
          </div>
          <div css={footerButtonStyle}>
            <Button text='Cancel' onClick={onClose} variant='secondary' />
          </div>
        </div>
      }
      styles={modalStyles}
    >
      <div css={bodyStyle}>
        <div css={searchFieldStyle}>
          <div css={labelRowStyle}>
            <Text variant='captionSmall' color={colors.WHITE} weight='semiBold'>
              PLAYER
            </Text>
            {showGlobalToggle ? (
              <button
                type='button'
                css={globalSearchButtonStyle}
                onClick={() => setIsGlobalSearch((prev) => !prev)}
              >
                <GlobalOutlined css={globeIconStyle} />
                <Text variant='paragraphXSmall' color={colors.WHITE}>
                  {globalSearchToggleLabel()}
                </Text>
                <Switch size='small' checked={isGlobalSearch} />
              </button>
            ) : null}
          </div>
          <div css={searchInputWrapperStyle}>
            <SearchOutlined css={searchIconStyle} />
            <AutoComplete
              ref={selectRef as never}
              css={autoCompleteStyle}
              placeholder={capitalizeWords('Search For a Player')}
              value={inputValue}
              onSearch={handleSearch}
              onSelect={handleSelect}
              notFoundContent={null}
              options={options}
              allowClear
              onClear={() => {
                setInputValue('')
                setQuery('')
              }}
            />
          </div>
        </div>

        {/* The box is always present; results render in the antd dropdown popup
            above the field. 'hidden' (results exist) falls back to the idle
            copy so the box never disappears. */}
        <div css={stateCardStyle}>
          <PersonSearchIcon size={40} color={colors.NEUTRAL_50} />
          {cardState === 'searching' ? (
            <Text
              variant='paragraphMedium'
              weight='medium'
              color={colors.WHITE}
            >
              Searching...
            </Text>
          ) : (
            <div css={stateCardTextStyle}>
              <Text
                variant='paragraphMedium'
                weight='medium'
                color={colors.WHITE}
              >
                {cardState === 'empty' ? 'No Players Found' : 'Find a Player'}
              </Text>
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
                style='text-align: center;'
              >
                {cardState === 'empty'
                  ? 'Try a different search or use New Player below.'
                  : 'Start typing to search for an existing player.'}
              </Text>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default PlayerSearchModal

/** One search result row: avatar, name, dob, id, and the player's teams. */
const PlayerOption: React.FC<{ player: AdminPlayerSearchResult }> = ({
  player,
}) => {
  const birthDate = player.birth_date
    ? dayjs.utc(player.birth_date).format('YYYY-MM-DD')
    : ''
  const teamsInfo = player.teams?.length
    ? player.teams
        .slice(0, 3)
        .map((team) => `${team.organization}: ${team.name}`)
        .join(' | ') + (player.teams.length > 3 ? ' ...' : '')
    : ''
  return (
    <div css={optionStyle}>
      <div css={optionTopRowStyle}>
        <img
          src={getImageThumbnailId(player) || PLACEHOLDER}
          alt={`${player.name_first} ${player.name_last}`}
          css={optionAvatarStyle}
          onError={(e) => {
            const img = e.target as HTMLImageElement
            img.onerror = null
            img.src = PLACEHOLDER
          }}
        />
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          {`${player.name_first} ${player.name_last}`}
        </Text>
        {birthDate ? (
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {` (${birthDate})`}
          </Text>
        ) : null}
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          {` (${player.id})`}
        </Text>
      </div>
      {teamsInfo ? (
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_100}
        >
          {teamsInfo}
        </Text>
      ) : null}
    </div>
  )
}

// ---- styled ----
const modalStyles = {
  // Mirrors AdminPlayerRegisterModal exactly: header has horizontal padding only
  // (vertical comes from antd's default, which is tighter), and the content
  // wrapper is zeroed horizontally with a small top pad so the section dividers
  // run edge to edge.
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  mask: { backgroundColor: 'rgba(6, 10, 16, 0.8)' },
  // Symmetric 16px horizontal padding matches the header + body so the X, the
  // New Player button, and the Close button all share the same right edge (and
  // the title / PLAYER label / search field share the same left edge).
  footer: {
    backgroundColor: colors.SECONDARY_300,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingTop: '16px',
  },
} as const

const modalCss = css`
  /* The title is a <p> (Text); kill its default margin so it aligns with the
     close icon like the original. All section paddings/dividers come from
     modalStyles above (mirrors AdminPlayerRegisterModal). The body gets a touch
     more bottom padding so the search field clears the footer divider. */
  .ant-modal-title p {
    margin: 0;
  }
  /* Align the close (X) right edge with the New Player / Close buttons (all 16px
     from the modal edge). */
  .ant-modal-close {
    inset-inline-end: 16px;
  }
  /* Body top padding (16px) is the gap above PLAYER; the gap below the card is
     owned by the footer's own 16px top padding, so the body adds none — keeping
     the card-to-footer gap equal to the label-to-top gap. */
  .ant-modal-body {
    padding: 16px 16px 0;
  }
`

// Two footer buttons share the row: New Player (left) and Cancel (right).
const footerStyle = css`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`

// Compact on desktop (Button has flex:1, so a sized wrapper keeps it from
// stretching full width); full width on mobile.
const footerButtonStyle = css`
  display: flex;
  width: 160px;
  @media (max-width: 768px) {
    width: 100%;
  }
`

// New Player reuses the shared Button (secondary = transparent + outline), but
// Figma outlines it in PRIMARY rather than white and keeps the label on one line.
const newPlayerButtonStyle = css`
  border-color: ${colors.PRIMARY};
  white-space: nowrap;
`

const newPlayerIconStyle = css`
  color: ${colors.WHITE};
  font-size: 20px;
`

const closeIconStyle = css`
  color: ${colors.SOLITUDE};
  font-size: 18px;
  &:hover {
    color: ${colors.WHITE};
  }
`

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

// PLAYER label (left) + Global Search toggle (right) on one row, per Figma.
const labelRowStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const globalSearchButtonStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
`

const globeIconStyle = css`
  color: ${colors.WHITE};
  font-size: 16px;
`

const searchFieldStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`

// Wraps the AutoComplete so the search icon can sit inside the field, left.
const searchInputWrapperStyle = css`
  position: relative;
  width: 100%;
`

const searchIconStyle = css`
  position: absolute;
  left: 16px;
  top: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  line-height: 0;
  color: ${colors.NEUTRAL_50};
  font-size: 18px;
  pointer-events: none;
  /* antd wraps the glyph in a <span>; flatten its line-box so the svg centers. */
  svg {
    display: block;
  }
`

const autoCompleteStyle = css`
  width: 100%;
  /* Force the select ROOT to the field height so the wrapper (and the absolutely
     positioned search icon anchored to it) is exactly 50px — otherwise the root
     collapses to ~40px and the icon centers above the placeholder. */
  &.ant-select {
    height: 50px !important;
  }
  .ant-select-selector {
    background: ${palette.SECONDARY_150} !important;
    border: 1px solid ${SEARCH_FIELD_BORDER} !important;
    border-radius: 8px !important;
    height: 50px !important;
    padding-left: 44px !important;
  }
  .ant-select-selection-search {
    display: flex;
    align-items: center;
    inset-inline-start: 44px !important;
  }
  .ant-select-selection-search-input {
    height: 48px !important;
    font-size: 14px !important;
    color: ${colors.WHITE} !important;
  }
  .ant-select-selection-placeholder {
    line-height: 48px !important;
    inset-inline-start: 44px !important;
    color: ${colors.NEUTRAL_50} !important;
  }
  .ant-select-clear {
    position: absolute;
    right: 12px;
    top: 24px;
  }
`

// Empty / no-results card below the field (Figma secondary-400 #212935).
const stateCardStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  background-color: ${palette.SECONDARY_350};
`

const stateCardTextStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`

const optionStyle = css`
  align-self: center;
  margin-top: 2px;
`

const optionTopRowStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`

const optionAvatarStyle = css`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-right: 4px;
`
